import { Coords } from 'src/types';
import { PROJECTED_TILE_SIZE, UNPROJECTED_TILE_SIZE } from 'src/config';

export interface DirectionIcon {
    x: number;
    y: number;
    rotation: number;
}

export interface ConnectorArrows {
    start: DirectionIcon | null;
    end: DirectionIcon | null;
}

export type ConnectorLabelPlacement = DirectionIcon;

interface ConnectorLabelPlacementOptions {
    obstacleTiles?: Coords[];
    labelWidthPx?: number;
}

const getProjectedSegment = (from: Coords, to: Coords) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;

    return {
        x: (dy - dx) * PROJECTED_TILE_SIZE.width / 2,
        y: (dx + dy) * PROJECTED_TILE_SIZE.height / 2
    };
};

const normalizeRotation = (rotation: number) => {
    return ((rotation + 180) % 360 + 360) % 360 - 180;
};

const getArrowAlongPath = (pathTiles: Coords[]): DirectionIcon | null => {
    const segments = pathTiles.slice(0, -1).map((from, index) => {
        const to = pathTiles[index + 1];
        const projected = getProjectedSegment(from, to);
        return {
            from,
            to,
            renderedLength: Math.hypot(projected.x, projected.y)
        };
    }).filter((segment) => segment.renderedLength > 0);

    if (segments.length === 0) return null;

    const totalLength = segments.reduce(
        (sum, segment) => sum + segment.renderedLength,
        0
    );
    // Walk backward across as many waypoint segments as necessary. This keeps
    // a short final waypoint segment from forcing the arrow under the icon.
    let remainingClearance = Math.min(56, totalLength / 2);

    for (let index = segments.length - 1; index >= 0; index -= 1) {
        const segment = segments[index];
        if (remainingClearance > segment.renderedLength) {
            remainingClearance -= segment.renderedLength;
            continue;
        }

        const insetRatio = remainingClearance / segment.renderedLength;
        const dx = segment.to.x - segment.from.x;
        const dy = segment.to.y - segment.from.y;
        const x = segment.to.x - dx * insetRatio;
        const y = segment.to.y - dy * insetRatio;

        return {
            x: x * UNPROJECTED_TILE_SIZE + UNPROJECTED_TILE_SIZE / 2,
            y: y * UNPROJECTED_TILE_SIZE + UNPROJECTED_TILE_SIZE / 2,
            rotation: (Math.atan2(dy, dx) * 180) / Math.PI + 90
        };
    }

    return null;
};

export const getConnectorDirectionIcon = (
    pathTiles: Coords[]
): DirectionIcon | null => {
    if (pathTiles.length < 2) return null;
    return getArrowAlongPath(pathTiles);
};

export const getConnectorStartArrow = (
    pathTiles: Coords[]
): DirectionIcon | null => {
    if (pathTiles.length < 2) return null;

    return getArrowAlongPath([...pathTiles].reverse());
};

export const getConnectorEndArrow = (
    pathTiles: Coords[]
): DirectionIcon | null => {
    return getConnectorDirectionIcon(pathTiles);
};

export const getConnectorArrows = (
    pathTiles: Coords[],
    arrowType: 'NONE' | 'SINGLE' | 'DOUBLE' = 'SINGLE',
    arrowDirection: 'forward' | 'backward' = 'forward'
): ConnectorArrows => {
    if (arrowType === 'NONE') {
        return { start: null, end: null };
    }

    if (arrowType === 'DOUBLE') {
        return {
            start: getConnectorStartArrow(pathTiles),
            end: getConnectorEndArrow(pathTiles)
        };
    }

    if (arrowDirection === 'backward') {
        return {
            start: getConnectorStartArrow(pathTiles),
            end: null
        };
    }

    return {
        start: null,
        end: getConnectorDirectionIcon(pathTiles)
    };
};

export const getConnectorLabelPlacement = (
    pathTiles: Coords[],
    options: ConnectorLabelPlacementOptions = {}
): ConnectorLabelPlacement | null => {
    if (pathTiles.length < 2) return null;

    const segments = pathTiles.slice(0, -1).map((from, index) => {
        const to = pathTiles[index + 1];
        const projected = getProjectedSegment(from, to);

        return {
            from,
            to,
            projected,
            length: Math.hypot(projected.x, projected.y)
        };
    }).filter((segment) => segment.length > 0);

    const totalLength = segments.reduce((sum, segment) => {
        return sum + segment.length;
    }, 0);

    if (totalLength === 0) return null;

    const getPlacement = (
        segment: (typeof segments)[number],
        progress: number
    ): ConnectorLabelPlacement & { tile: Coords } => {
        const tileX = segment.from.x
            + (segment.to.x - segment.from.x) * progress;
        const tileY = segment.from.y
            + (segment.to.y - segment.from.y) * progress;
        let rotation = Math.atan2(
            segment.to.y - segment.from.y,
            segment.to.x - segment.from.x
        ) * 180 / Math.PI;

        // The SVG is mirrored before its isometric projection. Flip the
        // label by 180 degrees when needed so its screen-space baseline reads
        // left-to-right rather than upside down.
        if (
            segment.projected.x > 0
            || (Math.abs(segment.projected.x) < 0.001 && segment.projected.y > 0)
        ) {
            rotation += 180;
        }

        return {
            x: tileX * UNPROJECTED_TILE_SIZE + UNPROJECTED_TILE_SIZE / 2,
            y: tileY * UNPROJECTED_TILE_SIZE + UNPROJECTED_TILE_SIZE / 2,
            rotation: normalizeRotation(rotation),
            tile: { x: tileX, y: tileY }
        };
    };

    const midpoint = totalLength / 2;
    let midpointTraversed = 0;
    let midpointPlacement: ReturnType<typeof getPlacement> | null = null;
    for (const segment of segments) {
        if (midpointTraversed + segment.length < midpoint) {
            midpointTraversed += segment.length;
            continue;
        }
        midpointPlacement = getPlacement(
            segment,
            (midpoint - midpointTraversed) / segment.length
        );
        break;
    }

    if (!midpointPlacement) return null;
    if (!options.obstacleTiles?.length) {
        return {
            x: midpointPlacement.x,
            y: midpointPlacement.y,
            rotation: midpointPlacement.rotation
        };
    }

    const candidates: Array<ConnectorLabelPlacement & {
        tile: Coords;
        pathDistance: number;
    }> = [{ ...midpointPlacement, pathDistance: midpoint }];
    let traversed = 0;
    for (const segment of segments) {
        // Keep descriptions in the central 30–70% of a segment. Positions
        // nearer the ends are easily covered by endpoint icons and labels.
        for (const progress of [0.5, 0.35, 0.65]) {
            candidates.push({
                ...getPlacement(segment, progress),
                pathDistance: traversed + segment.length * progress
            });
        }
        traversed += segment.length;
    }

    const obstacles = options.obstacleTiles ?? [];
    const labelRadius = Math.min(110, Math.max(52, (options.labelWidthPx ?? 120) / 2));
    const scoreCandidate = (candidate: (typeof candidates)[number]) => {
        const clearance = obstacles.length === 0
            ? Number.POSITIVE_INFINITY
            : Math.min(...obstacles.map((obstacle) => {
                const projected = getProjectedSegment(obstacle, candidate.tile);
                return Math.hypot(projected.x, projected.y) - labelRadius;
            }));
        const midpointPenalty = Math.abs(candidate.pathDistance - totalLength / 2) * 0.2;
        return clearance - midpointPenalty;
    };

    const best = candidates.reduce((current, candidate) =>
        scoreCandidate(candidate) > scoreCandidate(current) ? candidate : current
    );

    return { x: best.x, y: best.y, rotation: best.rotation };
};
